import { call, delay, put, select } from 'redux-saga/effects';
import { fetchActivityLogsFailure, fetchActivitySuccess } from '../actions';
import { ApiError } from 'store/api/types';
import { FetchActivityLogsRequestAction } from '../types';
import { checkResponse } from 'store/utils';

import { GetActivityLogsResponse, getActivityLogs } from '../api';
import { turnMessageOn } from 'store/app/actions';
import { MessageData, ProfilePicture } from 'store/app/types';
import { PICTURE_DELAY_TIME } from 'store/app/constants';
import { ActivityLog } from '../models';
import { getProfilePictures } from 'store/app/selectors';
import { fetchProfilePictureRequest } from 'store/account/actions';
import { getAuthUser } from 'store/auth/selectors';
import { AccountType } from 'store/api/enums';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* fetchActivityLogsSaga(action: FetchActivityLogsRequestAction) {
  try {
    let err: ApiError | null;
    const { dataLogId } = action.payload;
    const currentUser = yield select(getAuthUser);
    const accountType = currentUser?.accountType;
    const isClientUser =
      accountType === AccountType.CLIENT ||
      AccountType[accountType] === AccountType.CLIENT;

    // Clients do not have activity log UI in VAT report.
    // Guard at saga level to prevent unnecessary API calls.
    if (isClientUser) {
      yield put(fetchActivitySuccess([]));
      return;
    }

    const response: GetActivityLogsResponse = yield call(
      getActivityLogs,
      dataLogId
    );

    err = checkResponse(response?.responseMessage);
    if (err) throw err;

    const activityLogs: ActivityLog[] = response.data.activities;

    let pp: ProfilePicture[] = yield select(getProfilePictures);
    const requestedUserUuids = new Set<string>();
    let newActivityLogs: ActivityLog[] = [...activityLogs];
    for (let i = 0; i < activityLogs.length; i++) {
      const user = activityLogs[i].userDto;
      if (user && user.hasProfilePicture && !user.profilePicture) {
        const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
        if (ppIdx > -1) {
          const profilePicture = pp[ppIdx].profilePicture;
          newActivityLogs[i].userDto = {
            ...newActivityLogs[i].userDto,
            profilePicture,
          };
        } else if (!requestedUserUuids.has(user.userUuid)) {
          requestedUserUuids.add(user.userUuid);
          yield put(fetchProfilePictureRequest(user.userUuid));
          yield delay(PICTURE_DELAY_TIME);
          pp = yield select(getProfilePictures);
          const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
          if (ppIdx > -1) {
            const profilePicture = pp[ppIdx].profilePicture;
            newActivityLogs[i].userDto = {
              ...newActivityLogs[i].userDto,
              profilePicture,
            };
          }
        }
      }
    }
    // Single SUCCESS — avoid two full list updates (was: raw list then enriched list).
    yield put(fetchActivitySuccess(newActivityLogs));
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    yield put(fetchActivityLogsFailure());
  }
}
